import React from "react";
import PhoneNumberInput from "react-phone-number-input";
import { libphonenumberFilteredMetadata } from "./twilio-geo-permissions";
import { getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { Field, reset } from "redux-form";
import { Link, withRouter } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";

import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import * as validators from "./validators";
import { normaliseInstagramHandle } from "./validators";
import { HomeCityField } from "./cities-auto-complete";
import { Cta, FieldShell, TextField, showError, SubmitError } from "../fields";

// Shared with the web login form so both pages format a number the same way.
export const formatPhoneNumber = (value) => {
  if (!value) return "";
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() && parsed.country
    ? `+${getCountryCallingCode(parsed.country)} ${parsed.nationalNumber}`
    : value;
};

export const CountryCode = ({ country }) => (
  <span style={{ color: "white" }}>+{getCountryCallingCode(country)}</span>
);

const PhoneField = ({ input, meta, label, metadata }) => (
  <FieldShell id={input.name} label={label} meta={meta}>
    <div
      className={`${styles.box} ${styles.phoneField} ${showError(meta) ? styles.boxInvalid : ""}`}
    >
      <PhoneNumberInput
        {...input}
        onChange={(value) => input.onChange(formatPhoneNumber(value))}
        id={input.name}
        country="GB"
        international={false}
        displayInitialValueAsLocalNumber
        limitMaxLength
        metadata={metadata}
        type="tel"
        placeholder="07123 456789"
        flagComponent={CountryCode}
      />
    </div>
  </FieldShell>
);

const ConsentField = ({ input, meta }) => (
  <div className={styles.field} style={{ marginTop: 4 }}>
    <label className={styles.consent}>
      <input
        type="checkbox"
        className={styles.checkbox}
        {...input}
        checked={!!input.value}
      />
      <span>
        I agree to the <Link to="/terms-of-use">Terms of Use</Link>,{" "}
        <Link to="/privacy-policy">Privacy Policy</Link> and{" "}
        <Link to="/cookie-policy">Cookie Policy</Link>.
      </span>
    </label>
    {showError(meta) && (
      <div className={`${styles.error} ${styles.consentError}`}>{meta.error}</div>
    )}
  </div>
);

class StepFirst extends React.Component {
  state = {
    turnstileError: false,
    accountExists: false,
    submitMessage: "",
    isEmpty: false,
  };

  TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === "true";
  turnstileRef = React.createRef();
  isSubmitting = false;
  customMetadata = libphonenumberFilteredMetadata();

  getTurnstileToken = async () => {
    if (!this.TURNSTILE_ENABLED) return null;
    const widget = this.turnstileRef.current;
    widget.reset();
    widget.execute();
    const token = await widget.getResponsePromise();
    widget.reset();
    return token;
  };

  handleChange = (value) => {
    this.setState({ accountExists: false, submitMessage: "", isEmpty: !value });
  };

  async onSubmit(values, dispatch, props) {
    // Guard against the double-submit caused by the form/button both firing handleSubmit,
    // otherwise signup sends two SMS and two emails.
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.setState({ turnstileError: false, accountExists: false, submitMessage: "" });
    try {
      if (this.state.isEmpty) return;

      let turnstileToken;
      try {
        turnstileToken = await this.getTurnstileToken();
      } catch {
        this.setState({ turnstileError: true });
        return;
      }

      const normalised = Object.assign({}, values, {
        signupInstagramHandle: normaliseInstagramHandle(values.signupInstagramHandle),
        referrer: normaliseInstagramHandle(values.referrer),
      });
      const formattedValues = WithUserForm.formatUserGeneralInformationInput(normalised);
      const result = await props.updateUserGeneralInformation({
        variables: { input: formattedValues, turnstileToken },
      });
      window.localStorage.setItem("token", result.data.updateUserGeneralInformation.auth.token);
      props.machine.service.send({ type: "NEXT" });
      await props.userQuery.refetch();
    } catch (e) {
      if (e.graphQLErrors) {
        // JWT Token expired, remove it from local storage
        if (e.graphQLErrors[0]?.extensions?.reason === "JWT_EXPIRED") {
          localStorage.removeItem("token");
        } else {
          this.setState({ accountExists: true, submitMessage: e.graphQLErrors[0].message });
        }
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  renderSubmitError() {
    if (this.state.turnstileError) {
      return <SubmitError>We couldn't verify you're human. Please try again.</SubmitError>;
    }
    if (this.state.accountExists) {
      return (
        <SubmitError>
          {this.state.submitMessage || "An account already exists."}{" "}
          <Link to="/login">Log in here</Link>.
        </SubmitError>
      );
    }
    return null;
  }

  render() {
    return (
      <>
        {this.TURNSTILE_ENABLED && (
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
            <Turnstile
              ref={this.turnstileRef}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              options={{ appearance: "interaction-only", execution: "execute" }}
            />
          </div>
        )}
        <WithUserForm
          formRequiresAuthentication={false}
          onSubmit={(values, dispatch, props) => this.onSubmit(values, dispatch, props)}
        >
          {({ form: { handleSubmit, submitFailed, invalid, submitting }, dispatch }) => {
            const resetForm = () => dispatch(reset("apply"));
            const submitError = this.renderSubmitError();
            const footer = (
              <>
                {submitError}
                {!submitError && submitFailed && invalid && (
                  <SubmitError>Some fields need your attention.</SubmitError>
                )}
                <div className={styles.actions}>
                  <Cta submitting={submitting}>Continue</Cta>
                  <p className={styles.footerNote}>
                    Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
                  </p>
                </div>
              </>
            );

            return (
              <ApplicationLayout
                as="form"
                onSubmit={handleSubmit}
                step={1}
                title="Apply for membership"
                intro={<>Already started an application? <Link to="/login" onClick={resetForm}>Sign in</Link></>}
                footer={footer}
              >
                <div className={styles.fieldRow}>
                  <Field
                    name="firstName"
                    component={TextField}
                    label="First name"
                    autoComplete="given-name"
                    autoCapitalize="words"
                    spellCheck="false"
                    validate={validators.firstName}
                  />
                  <Field
                    name="lastName"
                    component={TextField}
                    label="Family name"
                    autoComplete="family-name"
                    autoCapitalize="words"
                    spellCheck="false"
                    validate={validators.lastName}
                  />
                </div>

                <Field
                  name="phoneNumber"
                  component={PhoneField}
                  label="Mobile number"
                  validate={validators.phone}
                  metadata={this.customMetadata}
                  onChange={this.handleChange}
                />

                <Field
                  name="email"
                  component={TextField}
                  label="Email"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck="false"
                  validate={validators.email}
                />

                <Field
                  name="homeCity"
                  component={HomeCityField}
                  label="Home-base city"
                  placeholder="Start typing, e.g. London"
                  hint="Where you live and keep your wardrobe: the city where you're available for work without travelling."
                  validate={validators.homeCity}
                />

                <Field
                  name="signupInstagramHandle"
                  component={TextField}
                  label="Your main Instagram"
                  prefix="@"
                  placeholder="handle or profile link"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  hint="The account you use to promote yourself or your brand."
                  validate={validators.instagram}
                />

                <Field
                  name="referrer"
                  component={TextField}
                  label="Referring member's Instagram"
                  prefix="@"
                  placeholder="handle or profile link"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  hint="The Schmodel member who is referring you. Every applicant needs a referral from an existing member."
                  validate={validators.referrer}
                />

                <Field
                  name="acceptedLegalAgreements"
                  component={ConsentField}
                  normalize={(v) => !!v}
                  format={(v) => !!v}
                  validate={validators.consent}
                />
              </ApplicationLayout>
            );
          }}
        </WithUserForm>
      </>
    );
  }
}

StepFirst = withRouter(StepFirst);

export default StepFirst;
